import { useEffect, useMemo, useRef, useState, lazy, Suspense } from 'react';
import { Outlet } from 'react-router-dom';
import { X, UserCog, ChevronRight, ArrowLeft } from 'lucide-react';
import { ContainerSizeContext, useContainerSizeObserver } from '@/hooks/use-container-size';
import { Header } from './header';
import { InboxNav } from './inbox-nav';
import { useAppInit } from '@/hooks/use-app-init';
import { useTheme } from '@/hooks/use-theme';
import { useRealtimeListeners } from '@/hooks/use-realtime-listeners';
import { useEventsInit } from '@/hooks/use-events-init';
import { useVoiceCall } from '@/hooks/use-voice-call';
import { useAppCursorCallBridge, useCallHostSizeReporter, IS_APPCURSOR_CALL_HOST } from '@/hooks/use-appcursor-call-bridge';
import { ErrorBoundary } from '@/components/common/error-boundary';
import { SaaSAuthPrompt } from '@/components/auth/saas-auth-prompt';
import { TPAAuthPrompt } from '@/components/auth/tpa-auth-prompt';
import { OrgSetupGate } from '@/components/auth/org-setup-gate';
import { useOrgSetupGateVisible } from '@/stores/org-join-store';
import { OrgMismatchOverlay } from '@/components/auth/org-mismatch-overlay';
import { CreditsBlockerDialog } from '@/components/common/credits-blocker-dialog';
import { AccountMismatchBanner } from '@/components/auth/account-mismatch-banner';
import { AccountHandoffBanner } from '@/components/auth/account-handoff-banner';
import { CredentialEntryDialog } from '@/components/common/credential-entry-dialog';
import { useModuleStore } from '@/stores/module-store';
import { TrialBanner } from '@/components/trial/trial-banner';
import { TrialPaywallModal } from '@/components/trial/trial-paywall-modal';
import { IncomingMessagePreview } from '@/components/common/incoming-message-preview';
import { ChatDialog } from '@/components/common/chat-dialog';
import { CallBar } from '@/components/voice/call-bar';
import { IncomingCallToast } from '@/components/voice/incoming-call-toast';
import { MissedCallToast } from '@/components/voice/missed-call-toast';
import { CallManager } from '@/components/voice/call-manager';
import { WhatsAppCallBar } from '@/components/voice/whatsapp-call-bar';
import { WhatsAppIncomingCallToast } from '@/components/voice/whatsapp-incoming-call';
import { FieldMappingFlowContent } from '@/pages/field-mapping-flow-page';
import { OrgJoinBanner } from '@/components/settings/org-join-banner';
const WorkflowWidgetPage = lazy(() =>
  import('@/components/settings/workflow-widget/workflow-widget-page')
    .then(m => ({ default: m.WorkflowWidgetPage }))
);
// Chat automation builder is rendered fullscreen here (no app Header) so the
// builder's own toolbar isn't stacked under a redundant top nav. AppPage no
// longer mounts it — see app-page.tsx where the CHAT_AUTOMATION branch was
// removed.
const ChatAutomationBuilder = lazy(() =>
  import('@/components/settings/chat-automation/chat-automation-builder')
    .then(m => ({ default: m.ChatAutomationBuilder }))
);
import { IncomingSyncAlert } from '@/components/common/incoming-sync-alert';
import { HighSenderSetupDialog } from '@/components/common/high-sender-setup-dialog';
import { useOnboardingStore } from '@/stores/onboarding-store';
import { OnboardingTimeline } from '@/components/onboarding/onboarding-timeline';
import { OnboardingPrompt } from '@/components/onboarding/onboarding-prompt';
const SettingsView = lazy(() =>
  import('@/components/settings/settings-view')
    .then(m => ({ default: m.SettingsView }))
    .catch((err) => {
      if (!sessionStorage.getItem('chunk_reload')) {
        sessionStorage.setItem('chunk_reload', '1');
        window.location.reload();
        return new Promise<never>(() => {});
      }
      sessionStorage.removeItem('chunk_reload');
      throw err;
    })
);
import { NetworkStatsOverlay } from '@/components/debug/network-stats-overlay';
import { useUIStore } from '@/stores/ui-store';
import { useConversationStore } from '@/stores/conversation-store';
import { useAuthStore } from '@/stores/auth-store';
import { useAppStore } from '@/stores/app-store';
import { parseAppCode } from '@/lib/utils/url';
import { INBOX_SUPPORTED_TPAS, NON_MESSAGING_TPAS, CALL_APPS, EVENT_APPS, getTPADisplayName, getSaaSPrettyName, type ViewType } from '@/config/constants';
import { analytics } from '@/config/firebase';
import { logEvent } from 'firebase/analytics';
import { decodeConvIdFromHash, encodeConvIdForHash } from '@/lib/utils/format';
// ── App Picker — shown when appCode is missing but user has installed apps ──

function formatAppLabel(appCode: string): { tpa: string; saas: string; label: string } {
  const parsed = parseAppCode(appCode);
  if (!parsed) return { tpa: appCode, saas: '', label: appCode };

  const capitalize = (s: string) =>
    s.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  const tpa = capitalize(parsed.tpa);
  const saas = capitalize(parsed.saas);
  return { tpa, saas, label: `${tpa} for ${saas}` };
}

function AppPicker({ apps }: { apps: string[] }) {
  const items = useMemo(
    () => apps.map((code) => ({ code, ...formatAppLabel(code) })),
    [apps]
  );

  const handleSelect = (appCode: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set('appCode', appCode);
    window.location.href = url.toString();
  };

  return (
    <div className="flex items-center justify-center py-16">
      <div className="w-full max-w-sm space-y-4 px-6">
        <div className="text-center space-y-1">
          <h2 className="text-lg font-semibold text-foreground">Select an App</h2>
          <p className="text-sm text-muted-foreground">Choose which app to open</p>
        </div>
        <div className="space-y-2">
          {items.map(({ code, tpa, saas }) => (
            <button
              key={code}
              onClick={() => handleSelect(code)}
              className="w-full flex items-center justify-between rounded-lg border border-border bg-card px-4 py-3 text-left hover:bg-accent transition-colors group"
            >
              <div>
                <span className="text-sm font-medium text-foreground">{tpa}</span>
                {saas && (
                  <span className="text-sm text-muted-foreground"> for {saas}</span>
                )}
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Init Error or App Picker ──

function InitErrorOrPicker({ initError }: { initError: string }) {
  const user = useAuthStore((s) => s.user);
  const isMissingAppCode = initError === 'No appCode provided';
  const installedApps = user?.inAppCodes;

  if (isMissingAppCode && installedApps?.length) {
    return <AppPicker apps={installedApps} />;
  }

  return (
    <div className="flex items-center justify-center py-20">
      <div className="max-w-md text-center space-y-4 p-8">
        <h2 className="text-xl font-semibold text-destructive">Initialization Error</h2>
        <p className="text-sm text-muted-foreground">{initError}</p>
        <p className="text-xs text-muted-foreground">
          Make sure you have a valid appCode in the URL, e.g.,{' '}
          <code className="bg-muted px-1 py-0.5 rounded">?appCode=twilioforzohocrm</code>
        </p>
      </div>
    </div>
  );
}

export function AppLayout() {
  const { initError, ready } = useAppInit();
  useTheme();
  useRealtimeListeners();
  useVoiceCall();
  useAppCursorCallBridge();

  // `?openChatId=<convId>` → open the chat dialog on mount, then strip the
  // param so a reload doesn't reopen it. Used by the "Open in new tab" button
  // in notification-panel and by any external link targeting a conversation.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('openChatId');
    if (!id) return;
    useUIStore.getState().openChatDialog(id);
    params.delete('openChatId');
    const qs = params.toString();
    const newUrl = `${window.location.pathname}${qs ? '?' + qs : ''}${window.location.hash}`;
    window.history.replaceState(null, '', newUrl);
  }, []);

  // `#<base64(convId)>` on an inbox-like URL → restore the conversation on
  // mount. Makes a conversation reload-stable AND shareable, without leaking
  // raw phone numbers into the URL bar / browser history / logs — ids are
  // `sender::contact` phone pairs, so they're base64-encoded before hitting
  // the URL. The encoding is deterministic, so users can paste the URL to a
  // colleague and it opens the same conversation.
  //
  // On the Inbox view we select the conversation in-place (prepending it to
  // the list if it isn't loaded yet) rather than opening a floating dialog.
  // Skip `#workflow-templates` — reserved by the workflow widget view.
  const tpaReady = useModuleStore((s) => !!s.tpaModule);
  const hashRestoredRef = useRef(false);
  useEffect(() => {
    if (!tpaReady || hashRestoredRef.current) return;
    hashRestoredRef.current = true;
    const isAppCursor = new URLSearchParams(window.location.search).get('target') === 'appcursor';
    // Path gating: inbox route OR appcursor target (which runs as a compact
    // Chrome-extension popup that shouldn't show the inbox list — it only
    // hosts a single chat, so we skip the /inbox check when appcursor is on).
    const isInboxUrl = window.location.pathname.includes('/inbox')
      || new URLSearchParams(window.location.search).get('force-ui-view') === 'CONVERSATIONS';
    if (!isAppCursor && !isInboxUrl) return;
    const rawHash = window.location.hash.replace(/^#/, '');
    if (!rawHash) return;
    const hashToken = decodeURIComponent(rawHash.split(/[?&/]/)[0]);
    if (!hashToken || hashToken === 'workflow-templates') return;
    const hashId = decodeConvIdFromHash(hashToken);
    if (!hashId) return;

    // AppCursor target → show only the chat view (no inbox list).
    if (isAppCursor) {
      useUIStore.getState().setView('CHAT');
    }

    const convStore = useConversationStore.getState();
    const existing = convStore.conversations.find((c) => c.id === hashId);
    if (existing) {
      convStore.setActiveConversation(existing);
      // Inbox-side rule: only mark-as-read when the conv had an unread
      // badge. Avoids a CF roundtrip every time a deep-link reopens an
      // already-read chat (e.g. browser back/forward, share-link reload).
      if (existing.unreadCount > 0) {
        convStore.markAsRead(hashId);
      }
      return;
    }

    let cancelled = false;
    convStore.resolveConversationById(hashId).then((conv) => {
      if (cancelled) return;
      const latest = useConversationStore.getState();
      if (!latest.conversations.some((c) => c.id === conv.id)) {
        useConversationStore.setState({ conversations: [conv, ...latest.conversations] });
      }
      useConversationStore.getState().setActiveConversation(conv);
      if (conv.unreadCount > 0) {
        useConversationStore.getState().markAsRead(conv.id);
      }
    }).catch(() => {
      // If resolve fails (Firestore read failure, bad id) leave the user on
      // the empty inbox rather than popping an error — the hash-sync effect
      // below will overwrite the stale token when another conv is selected.
    });
    return () => { cancelled = true; };
  }, [tpaReady]);

  const onboardingRequired = useOnboardingStore((s) => s.onboardingRequired);
  const activeView = useUIStore((s) => s.activeView);
  const settingsOpen = useUIStore((s) => s.settingsOpen);
  const closeSettings = useUIStore((s) => s.closeSettings);
  const isSettings = settingsOpen;
  const isInbox = activeView === 'CONVERSATIONS' || activeView === 'CHAT';

  // Detect workflow templates widget mode (#workflow-templates hash or legacy ?actionType=workflow_widget)
  // NOTE: Some CRMs (e.g. Zoho) append their own query params AFTER our hash fragment,
  // producing URLs like "#workflow-templates?zohoParam=value" or "#workflow-templates&x=y".
  // Extract only the hash identifier (up to the first ?, &, /, or #) so we match correctly.
  // Declared here (not below with the other memos) because the conversation
  // hash-sync effect right below needs to skip when this is true.
  const isWorkflowWidget = useMemo(() => {
    const rawHash = window.location.hash.replace(/^#/, '');
    const hashId = rawHash.split(/[?&/#]/)[0];
    const params = new URLSearchParams(window.location.search);
    return hashId === 'workflow-templates' ||
      params.get('actionType') === 'workflow_widget';
  }, []);

  // Sync `useConversationStore.activeConversation` → `window.location.hash`.
  //
  // The raw id (`sender::contact` phone pair) is base64-encoded before
  // writing to the URL so phone numbers never appear plainly in the URL bar,
  // browser history, or referrer logs. Encoding is deterministic so the URL
  // is still shareable — pasting it in another tab restores the same chat.
  //
  // When leaving inbox: only clear the hash if we were the ones who wrote
  // it (tracked via `lastWrittenConvHashRef`). This preserves hash
  // conventions owned by other views — e.g. settings-view.tsx uses
  // `#chat-automation` / `#integrations` / etc. to deep-link to a card.
  const activeConversationId = useConversationStore((s) => s.activeConversation?.id ?? null);
  const lastWrittenConvHashRef = useRef<string | null>(null);
  useEffect(() => {
    // Skip entirely when we're in the Workflow Templates widget — the
    // `#workflow-templates` hash is owned by that view, not the conversation
    // hash-sync. Without this guard, Zoho's custom-action URL
    //   `?force-ui-view=CONVERSATIONS#workflow-templates`
    // triggers this effect (isInbox=true via force-ui-view), and since there
    // is no active conversation the "else"/"nextToken empty" branch strips
    // `#workflow-templates` from the URL, breaking widget detection.
    if (isWorkflowWidget) return;

    // Skip when the Settings overlay is open — the hash belongs to
    // settings-view (e.g. `#chat-automation`, `#integrations`). Loading
    // `/settings#chat-automation` on an inbox-supporting TPA sets the
    // underlying `activeView` to `CONVERSATIONS`/`CHAT` before flipping
    // `settingsOpen`, which makes `isInbox=true` with no active conversation
    // — the branch below would otherwise replace the URL without the hash,
    // wiping the settings deep-link before SettingsView reads it.
    if (settingsOpen) return;

    const currentRaw = window.location.hash.replace(/^#/, '');
    const currentDecoded = currentRaw ? decodeURIComponent(currentRaw) : '';

    // Also defensively skip if the hash is the workflow-templates reserved id
    // (matches the guards on the other hash-handling effects in this file).
    const currentHashId = currentDecoded.split(/[?&/#]/)[0];
    if (currentHashId === 'workflow-templates') return;

    if (isInbox) {
      const nextToken = activeConversationId ? encodeConvIdForHash(activeConversationId) : '';
      if (currentDecoded === nextToken) {
        lastWrittenConvHashRef.current = nextToken || null;
        return;
      }
      // When there's no active conversation (nextToken === ''), only clear
      // the existing hash if WE wrote it. A hash placed by another view
      // (e.g. a settings deep-link that survived a navigation) must not be
      // clobbered just because the inbox happens to be mounted underneath.
      if (!nextToken && currentDecoded && currentDecoded !== lastWrittenConvHashRef.current) {
        return;
      }
      const newUrl = `${window.location.pathname}${window.location.search}${nextToken ? '#' + encodeURIComponent(nextToken) : ''}`;
      window.history.replaceState(null, '', newUrl);
      lastWrittenConvHashRef.current = nextToken || null;
    } else {
      // Leaving inbox — only clear the hash if it matches what we last wrote.
      // Anything else belongs to another view (e.g. settings deep-link).
      if (currentDecoded && currentDecoded === lastWrittenConvHashRef.current) {
        const newUrl = `${window.location.pathname}${window.location.search}`;
        window.history.replaceState(null, '', newUrl);
        lastWrittenConvHashRef.current = null;
      }
    }
  }, [activeConversationId, isInbox, isWorkflowWidget, settingsOpen]);

  // Legacy cleanup — if a previous version left a raw `#<phone::phone>` hash,
  // rewrite it to the encoded form so reload-stability keeps working without
  // the phone-number exposure.
  useEffect(() => {
    const rawHash = window.location.hash.replace(/^#/, '');
    if (!rawHash) return;
    const hashId = decodeURIComponent(rawHash.split(/[?&/]/)[0]);
    if (hashId === 'workflow-templates' || !hashId.includes('::')) return;
    const encoded = encodeConvIdForHash(hashId);
    const newUrl = `${window.location.pathname}${window.location.search}${encoded ? '#' + encodeURIComponent(encoded) : ''}`;
    window.history.replaceState(null, '', newUrl);
  }, []);

  const appCodeTPA = useAppStore((s) => s.appCodeTPA) ?? '';
  const inboxSupported = INBOX_SUPPORTED_TPAS.has(appCodeTPA);
  const isNonMessaging = NON_MESSAGING_TPAS.has(appCodeTPA);
  // Standalone LookUp app (lookupforzohocrm, lookupforhubspotcrm, …) has its
  // own top toolbar with profile + settings, so we hide the global Header,
  // legacy-version banner, and onboarding timeline. Other non-messaging TPAs
  // (googledrive, stripe) keep the standard chrome.
  const isLookupApp = appCodeTPA === 'lookup';
  const isCallApp = (CALL_APPS as readonly string[]).includes(appCodeTPA);
  const isEventApp = (EVENT_APPS as readonly string[]).includes(appCodeTPA);

  const impersonatedUserId = useAuthStore((s) => s.impersonatedUserId);
  const impersonatedUserEmail = useAuthStore((s) => s.impersonatedUserEmail);
  const setImpersonation = useAuthStore((s) => s.setImpersonation);

  // Detect actionType=fieldMapping from URL query params (same URL pattern as original app)
  const isFieldMapping = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('actionType') === 'fieldMapping';
  }, []);


  // In field mapping mode, EventsView never renders so useEvents() never runs.
  // Initialize the events store here (at AppLayout level, so it persists even
  // when Settings overlay replaces the flow builder). In normal mode the hook
  // is disabled and useEvents() in EventsView handles initialization instead.
  useEventsInit(isFieldMapping);

  const settingsDetailTitle = useUIStore((s) => s.settingsDetailTitle);
  const setSettingsDetailTitle = useUIStore((s) => s.setSettingsDetailTitle);

  // TPA credential dialog state — needed when settings opens directly (AppPage doesn't mount)
  const tpaModule = useModuleStore((s) => s.tpaModule);
  const tpaAuthenticated = useModuleStore((s) => s.tpaAuthenticated);
  const tpaAuthChecking = useModuleStore((s) => s.tpaAuthChecking);
  const tpaLoading = useModuleStore((s) => s.tpaLoading);
  const saasAuthenticated = useModuleStore((s) => s.saasAuthenticated);
  const saasModule = useModuleStore((s) => s.saasModule);
  const saasReady = saasAuthenticated || !saasModule;
  const orgGateVisible = useOrgSetupGateVisible();
  const showCredentialDialog =
    isSettings &&
    tpaModule !== null &&
    !tpaAuthenticated &&
    !tpaAuthChecking &&
    !tpaLoading &&
    saasReady &&
    !orgGateVisible &&
    !tpaModule.getOAuthInfo?.();

  // Settings is now an overlay — closing it just flips `settingsOpen` false
  // and re-syncs the URL to the underlying `activeView` (handled inside
  // `closeSettings`). The old "which view do we return to?" cascade is gone.
  const handleCloseSettings = () => {
    closeSettings();
  };

  const handleSettingsBack = () => {
    setSettingsDetailTitle(null);
  };
  const appCodeTPA_ = useAppStore((s) => s.appCodeTPA);
  const appCodeSAAS_ = useAppStore((s) => s.appCodeSAAS);
  // ── Page title: "{TPA} - {SAAS} | {View}" ──
  const crmRecordCount = useModuleStore((s) => s.crmRecords.length);
  useEffect(() => {
    // Wait until app codes are resolved before setting title / logging analytics
    if (!appCodeTPA_ || !appCodeSAAS_) return;
    // Hidden AppCursor call host loads in every CRM tab — not a page view.
    if (IS_APPCURSOR_CALL_HOST) return;
    const tpa = getTPADisplayName(appCodeTPA_);
    const saas = getSaaSPrettyName(appCodeSAAS_);
    const viewLabel: Record<string, string> = {
      MESSAGE_FORM: 'New message',
      CHAT: 'Chat',
      CONVERSATIONS: 'Inbox',
      SETTINGS: 'Settings',
      EVENTS: 'Events',
      GOOGLEDRIVE: 'Google drive',
      LOOKUP: 'Lookup',
      CRM_CHAT: 'Crm chat',
      STRIPE: 'Stripe',
      PHONE_TREE: 'Phone tree',
      CHAT_AUTOMATION: 'Chat automation',
      RECURRING_TASKS: 'Recurring tasks',
      RECURRING_TASKS_PANEL: 'Management panel',
    };
    let view = viewLabel[activeView] || activeView;
    if (activeView === 'MESSAGE_FORM' && crmRecordCount > 1) {
      view = 'Bulk message';
    }
    // Settings overrides the title when open, since the overlay covers the
    // underlying view.
    if (settingsOpen) {
      view = settingsDetailTitle || 'Settings';
    }
    // When loaded outside a CRM iframe (standalone tab), put the view first
    // and the app identity at the end — browsers/OS task switchers truncate
    // long titles from the right, and the view is what distinguishes tabs
    // for the user. Inside a CRM iframe nobody reads the title so the
    // original order (`{tpa} - {saas} | {view}`) is kept for consistency
    // with legacy analytics / history entries.
    const isStandalone = (() => {
      try { return window.self === window.top; } catch { return false; }
    })();
    document.title = isStandalone
      ? `${view} - ${tpa} - ${saas}`
      : `${tpa} - ${saas} | ${view}`;
    if (analytics) {
      logEvent(analytics, 'page_view', { page_title: document.title });
    }
  }, [appCodeTPA_, appCodeSAAS_, activeView, settingsOpen, settingsDetailTitle, crmRecordCount]);

  const rootRef = useRef<HTMLDivElement>(null);
  const containerSize = useContainerSizeObserver(rootRef);
  const { isCompact, isMini, isMicro, isShortHeight } = containerSize;

  // Hidden AppCursor call host (see use-appcursor-call-bridge.ts): only the
  // Twilio Device + CallBar. The extension draws the incoming-call toast and
  // shows this frame once a call is live.
  if (IS_APPCURSOR_CALL_HOST) {
    return <AppCursorCallHostView />;
  }

  return (
    <ContainerSizeContext.Provider value={containerSize}>
    <div
      ref={rootRef}
      className={`flex h-screen flex-col bg-background overflow-hidden${isCompact ? ' compact-view' : ''}${isMini ? ' mini-view' : ''}${isMicro ? ' micro-view' : ''}`}
    >
      {/* Onboarding timeline — steps 1-3, shown at top when setup incomplete.
          Skipped for the standalone LookUp app since it has no TPA credential
          or sender setup to walk through. */}
      {onboardingRequired && ready && !isLookupApp && <OnboardingTimeline />}
      {/* Onboarding prompt — steps 4-5 (country code + notification contact) and
          the ph-empty re-trigger from use-app-init. */}
      {ready && <OnboardingPrompt />}
      {/* Incoming sync alert — shown when background check detects no incoming configured */}
      {ready && <IncomingSyncAlert />}
      {ready && <HighSenderSetupDialog />}

      {/* Full-screen blocker when billing user is out of credits (< -25). */}
      <CreditsBlockerDialog />

      <OrgSetupGate isMini={isMini} />
      {!orgGateVisible && <SaaSAuthPrompt />}
      {/* Full-page overlay shown when the user authorized the SaaS in a
          different org than the one they're currently accessing. The CTA
          clears saasAuthenticated and dispatches saas:reauth-required so
          SaaSAuthPrompt (above) takes over and re-runs OAuth. */}
      <OrgMismatchOverlay />
      {/* Show TPA auth prompts when settings is active — AppPage (which normally renders them) doesn't mount in settings mode */}
      {isSettings && <TPAAuthPrompt />}
      {/* CredentialEntryDialog is rendered AFTER the Settings overlay below
         (see further down in this file) so at tied z-50 it paints on top. */}
      <TrialBanner />
      {!orgGateVisible && <OrgJoinBanner />}

      {/* Admin impersonation banner */}
      {impersonatedUserId && (
        <div className="flex items-center justify-center gap-2 bg-amber-500 text-white text-xs font-medium py-1 px-3 shrink-0 z-50">
          <UserCog className="h-3.5 w-3.5" />
          <span>Impersonating: {impersonatedUserEmail || impersonatedUserId}</span>
          <button
            onClick={() => setImpersonation(null)}
            className="ml-1 rounded px-1.5 py-0.5 hover:bg-amber-600 transition-colors"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      )}

      {/* Account mismatch — CRM user email differs from Firebase user */}
      <AccountMismatchBanner />

      {/* Same warning for the top-level case, where there's no CRM to ask:
          arrived from the marketplace / AppCursor via ?suggestLoginMail= under
          a different account than the one signed in here. Self-gates to
          non-iframe, so it never doubles up with the banner above. */}
      <AccountHandoffBanner sourceLabel="the account you came from" />

      {/* Shared admin access — no banner needed, this is normal for shared users */}

      {/* Underlying view — always rendered. Settings is a sibling overlay
         below so this subtree stays mounted while Settings is open
         (no refetch / scroll loss on open/close). */}
      {isWorkflowWidget ? (
        /* Workflow Custom Action Widget — full screen (#workflow-templates) */
        <Suspense fallback={<div className="flex h-full items-center justify-center"><div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>}>
          <WorkflowWidgetPage />
        </Suspense>
      ) : isFieldMapping ? (
        /* Field Mapping Flow — full screen with its own header (same as original app actionType=fieldMapping) */
        <FieldMappingFlowContent />
      ) : activeView === 'CHAT_AUTOMATION' ? (
        /* Chat Automation builder — full screen; the builder owns its toolbar
           so we skip the app Header to avoid stacking two top bars. */
        <Suspense fallback={<div className="flex h-full items-center justify-center"><div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>}>
          <ChatAutomationBuilder />
        </Suspense>
      ) : isInbox ? (
        /* Inbox layout: vertical left nav (md+) + mobile header (<md) */
        /* In compact container mode, skip sidebar and use header icons instead */
        /* When height < 500px, use sidebar instead of header to save vertical space */
        <>
          {/* Show top header in compact/mini view — hide when short height (use sidebar instead) */}
          {/* In non-compact (wide) view, header is hidden and sidebar is used */}
          {(isCompact || isMini) && !isShortHeight && <Header />}
          {/* Fallback: show header when isMini + shortHeight (sidebar too wide for narrow views) */}
          {isMini && isShortHeight && <Header />}
          <div className="flex flex-1 overflow-hidden">
            {/* Show sidebar when not compact OR when short height (saves vertical space), hide in mini view */}
            {!isMini && (!isCompact || isShortHeight) && <InboxNav />}
            <main className="flex-1 overflow-hidden">
              {initError ? (
                <InitErrorOrPicker initError={initError} />
              ) : (
                <ErrorBoundary>
                  <Outlet />
                </ErrorBoundary>
              )}
            </main>
          </div>
        </>
      ) : (
        /* Non-inbox layout: header only (form view).
           Standalone LookUp renders its own top toolbar, so we hide the
           global app header to avoid stacking two headers. Other non-messaging
           TPAs (googledrive, stripe) keep using the standard header. */
        <>
          {!isLookupApp && <Header />}
          <main className="flex-1 overflow-hidden">
            {initError ? (
              <InitErrorOrPicker initError={initError} />
            ) : (
              <ErrorBoundary>
                <Outlet />
              </ErrorBoundary>
            )}
          </main>
        </>
      )}

      {/* Settings overlay — fixed to the viewport so it always fills the page
         regardless of parent flex layout. z-50 matches the Header's z-index;
         since the overlay is later in the DOM, it paints on top. App-level
         dialogs (also z-50) are portaled to document.body and therefore
         render above this overlay. The underlying view stays mounted
         beneath it, so closing doesn't refetch or lose scroll/selection. */}
      {isSettings && (
        <div className="fixed inset-0 z-50 flex flex-col bg-background">
          {/* Settings header — grid shows "Settings", detail shows breadcrumb */}
          <header className="shrink-0 border-b border-border/60 bg-card/95 backdrop-blur-md">
            <div className={`flex items-center justify-between ${isCompact ? 'h-10 px-2.5' : 'h-12 px-4 lg:px-6'}`}>
              {settingsDetailTitle ? (
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleSettingsBack}
                    className="group flex items-center gap-1.5 rounded-lg px-2 py-1 text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-200 -ml-2"
                  >
                    <ArrowLeft className="h-4 w-4 transition-transform duration-200 group-hover:-translate-x-0.5" />
                    Settings
                  </button>
                  <span className="text-sm text-muted-foreground/40">/</span>
                  <span className="font-display text-sm font-medium text-foreground">{settingsDetailTitle}</span>
                </div>
              ) : (
                <div className="flex items-center gap-1.5">
                  {/* KAN-175: back arrow on the Settings home grid. Same
                      action as the X — closes the overlay and returns to
                      the app. Reporters found the X alone hard to spot. */}
                  <button
                    data-testid="settings-back"
                    onClick={handleCloseSettings}
                    className="group rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-200 -ml-1.5"
                    title="Back"
                    aria-label="Back"
                  >
                    <ArrowLeft className="h-4 w-4 transition-transform duration-200 group-hover:-translate-x-0.5" />
                  </button>
                  <h1 className="font-display text-lg font-semibold text-foreground">Settings</h1>
                </div>
              )}
              <button
                data-testid="settings-close"
                onClick={handleCloseSettings}
                className="group rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-200"
                title="Close settings"
              >
                <X className="h-5 w-5 transition-transform duration-200 group-hover:rotate-90" />
              </button>
            </div>
          </header>
          <main className="flex-1 min-h-0 overflow-hidden">
            <ErrorBoundary>
              <Suspense fallback={<div className="flex h-full items-center justify-center"><div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>}>
                <SettingsView />
              </Suspense>
            </ErrorBoundary>
          </main>
        </div>
      )}
      {/* Rendered after Settings overlay so at tied z-50 it paints on top.
         Needed when the Settings overlay is open and a TPA credential
         entry is required. */}
      <CredentialEntryDialog
        open={showCredentialDialog}
        dismissible
        onClose={() => useModuleStore.getState().checkTPAAuth()}
      />
      <IncomingMessagePreview />
      <ChatDialog />

      {/* Voice call overlays (global) */}
      <CallBar />
      <IncomingCallToast />
      <MissedCallToast />
      {!isMini && <CallManager />}

      {/* WhatsApp native calling overlays */}
      <WhatsAppCallBar />
      <WhatsAppIncomingCallToast />

      <TrialPaywallModal />

      {/* Debug network stats — gated by localStorage devModeViewNetworkInfo */}
      <NetworkStatsOverlay />

    </div>
    </ContainerSizeContext.Provider>
  );
}

/** Call host frame body: just the CallBar, measured so the extension can fit the frame to it. */
function AppCursorCallHostView() {
  const barRef = useRef<HTMLDivElement>(null);
  useCallHostSizeReporter(barRef);
  return (
    <div className="h-screen bg-transparent overflow-hidden">
      <div ref={barRef} className="w-max">
        <CallBar embedded />
      </div>
    </div>
  );
}
